import { AppBar, Box, Button, ButtonBase, Divider, FormControlLabel, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Switch, Toolbar, Typography, styled  } from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { useContext, useState } from "react";
import VillageConsoleContext from "../../store/VillageConsoleContext";
import { Domains } from "../../data/Domains";
import { useLocation, useNavigate } from "react-router-dom";
import InfoIcon from '@mui/icons-material/Info';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from "../../auth/useAuth";
import { AboutDialog } from "../AboutDialog";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import views from "../../data/views.data";


type HeaderProps = {

    openDrawer: () => void;
    userName: string;

}


const MaterialUISwitch = styled(Switch)(({ theme }) => ({
    width: 62,
    height: 34,
    padding: 7,
    '& .MuiSwitch-switchBase': {
      margin: 1,
      padding: 0,
      transform: 'translateX(6px)',
      '&.Mui-checked': {
        color: '#fff',
        transform: 'translateX(22px)',
        '& .MuiSwitch-thumb:before': {
          backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
            '#fff',
          )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
        },
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
        },
      },
    },
    '& .MuiSwitch-thumb': {
      backgroundColor: theme.palette.mode === 'dark' ? '#003892' : '#001e3c',
      width: 32,
      height: 32,
      '&:before': {
        content: "''",
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: 0,
        top: 0,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          '#fff',
        )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
      },
    },
    '& .MuiSwitch-track': {
      opacity: 1,
      backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
      borderRadius: 20 / 2,
    },
  }));


const stackSx = {

    cursor: 'pointer',
    paddingLeft:'15px',
    paddingRight:'15px',

    height: '48px',
    
    '&:hover': {
        backgroundColor: '#d44f00'
    }

};


export const Header: React.FC<HeaderProps> = ( props: HeaderProps ) => {

    const { openDrawer, userName } = props;

    
    const [ aboutDialogOpen, setAboutDialogOpen ] = useState< boolean >( false );
    
    const [ anchorAccountEl, setAnchorAccountEl ] = useState<null | HTMLElement>( null );
    const [ anchorDomainEl, setAnchorDomainEl ] = useState<null | HTMLElement>( null );
    const [ isDarkTheme, setIsDarkTheme ] = useState< boolean >( false );
    
    const { availableDomains, domain, setDomain } = useContext( VillageConsoleContext );
    
    const domainMenuOpen = Boolean( anchorDomainEl );

    const { logout } = useAuth( );
    const location = useLocation( );
    const navigate = useNavigate( );

    const view = views.find( view => view.path === location.pathname.toLowerCase( ) );


    const handleAboutClick = () => {

        handleMenuClose( );
        setAboutDialogOpen( true );

    };

    
    const handleAboutDialogOk = () => setAboutDialogOpen( false );


    const handleAccountButtonClick = ( event: React.MouseEvent<HTMLButtonElement> ) => setAnchorAccountEl( event.currentTarget );


    const handleMenuClose = ( ) => setAnchorAccountEl( null );

    
    const handleThemeChange = ( event: React.ChangeEvent<HTMLInputElement> ) => { 
        
        setIsDarkTheme( event.target.checked );
        // dispatch({ type: VraPortalActions.SetIsDarkTheme, payload: event.target.checked }); //TODO_DK: reducer context

    }

    const handleSignOutClick = async( ) => {
        
        setAnchorAccountEl( null );
        await logout();

    };


    const selectDomain = ( domain: Domains ) => {
        
        setAnchorDomainEl( null );
        setDomain( domain );

    }
    

    return (

        <>

            <AppBar
                position="absolute" sx={{backgroundColor: 'rgb(230, 84, 0);'}}>
                
                <Toolbar variant="dense" disableGutters>
                    
                    <IconButton
                        color="inherit"
                        aria-label="Open drawer"
                        onClick={ ( ) => openDrawer( ) }
                    >
                        <MenuIcon />
                    </IconButton>

                    <Typography
                        component="h1"
                        variant="h6"
                        color="inherit"
                        noWrap
                        sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                        onClick={ ( ) => navigate( '/' ) }
                    >
                        Village Rules Portal
                    </Typography>

                    {

                        ( view && view.text !== 'Home' )
                        &&
                        <>
                            <KeyboardArrowRightIcon />
                            <Typography
                                component="h1"
                                variant="h6"
                                color="inherit"
                                noWrap
                                sx={{ fontWeight: 'bold' }}
                            >
                                { view?.text }
                            </Typography>
                        </>

                    }

                    <Box sx={{ flexGrow: 1 }} />

                    <Divider orientation="vertical" flexItem />

                    { 
                        availableDomains.length !== 0
                            &&
                        <>
                            <Divider orientation="vertical" flexItem />

                            <Button sx={{color: 'white', fontWeight: 'bold'}}
                                onClick={( event: React.MouseEvent<HTMLButtonElement> ) => setAnchorDomainEl(event.currentTarget)}
                            >
                                {domain}
                            </Button>
                            
                            <Menu
                                anchorEl={ anchorDomainEl }
                                open={ domainMenuOpen }
                                onClose={ () => setAnchorDomainEl( null ) }
                            >

                                { availableDomains.includes( Domains.Bma ) && <MenuItem onClick={ ( ) => selectDomain( Domains.Bma ) }>{ Domains.Bma }</MenuItem> }
                                { availableDomains.includes( Domains.Niw ) && <MenuItem onClick={ ( ) => selectDomain( Domains.Niw ) }>{ Domains.Niw }</MenuItem> }
                                { availableDomains.includes( Domains.Waio ) && <MenuItem onClick={ ( ) => selectDomain( Domains.Waio ) }>{ Domains.Waio }</MenuItem> }

                            </Menu>
                        </>    
                    
                    }

                    <Divider orientation="vertical" flexItem />
                    
                    <ButtonBase onClick={ handleAccountButtonClick }>
                            <Stack direction="row" display="flex" alignItems="center" sx={ stackSx } >

                                <AccountCircle sx={{ paddingRight: '10px' }}/>
                                <Typography sx={{ fontWeight: 'bold' }}>{ userName }</Typography>
                                
                            </Stack>
                        </ButtonBase>

                        <Menu
                            anchorEl={ anchorAccountEl }
                            open={ Boolean( anchorAccountEl ) }
                            onClose={ handleMenuClose } 
                            MenuListProps={{
                                'aria-labelledby': 'basic-button',
                            }}
                            PaperProps={{
                                style: {
                                    width: '172px',
                                }
                            }}
                        >
                            <MenuItem disableRipple sx={{ marginTop: '-8px' }} disabled>
                                <FormControlLabel
                                    control={
                                        <MaterialUISwitch 
                                            sx={{ m: 1 }} 
                                            checked={ isDarkTheme }
                                            onChange={ handleThemeChange } />
                                    }
                                    label="Ui Theme"
                                />
                            </MenuItem>

                            <Divider sx={{ margin: '0px !important' }}/>

                            <MenuItem onClick={ handleAboutClick } sx={{ height: '62px' }} >
                                <ListItemIcon>
                                    <InfoIcon />
                                </ListItemIcon>
                                <ListItemText>About</ListItemText>
                            </MenuItem>

                            <Divider sx={{ margin: '0px !important' }}/>
                            
                            <MenuItem onClick={ handleSignOutClick } sx={{ height: '62px', marginBottom: '-8px' }} >
                                <ListItemIcon>
                                    <LogoutIcon />
                                </ListItemIcon>
                                <ListItemText>Sign-Out</ListItemText>
                            </MenuItem>

                        </Menu>

                </Toolbar>

            </AppBar>

            <AboutDialog 
                dialogOpen={ aboutDialogOpen }
                handleOk={ handleAboutDialogOk }  
            />

        </>

    );
};